import CloseIcon from "@mui/icons-material/Close"
import Alert from "@mui/material/Alert"
import CircularProgress from "@mui/material/CircularProgress"
import Drawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import SmsMessageCard from "../../customer/settings/SmsMessageCard"
import { adminApi } from "../axios"
import {
  counterpartyLabel,
  threadMessageToCard,
} from "../concierge-detail/activityFeed"

function threadLoadError(error) {
  const detail = error?.response?.data?.detail
  return typeof detail === "string" && detail.trim()
    ? detail
    : "Could not load thread."
}

/** Side drawer with every text to and from one lead (inbox + CRM texts). */
export default function AdminActivityThreadDrawer({ thread, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!thread) {
      setData(null)
      setError(null)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    adminApi
      .fetchActivityThread({ kind: thread.kind, id: thread.id })
      .then(({ data: payload }) => {
        if (!cancelled) setData(payload)
      })
      .catch((loadError) => {
        if (!cancelled) {
          setData(null)
          setError(threadLoadError(loadError))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [thread])

  const messages = data?.messages || []

  return (
    <Drawer
      anchor="right"
      open={Boolean(thread)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
    >
      <Column gap={2} sx={{ p: 2.5, minHeight: "100%" }}>
        <Row sx={{ alignItems: "flex-start", gap: 1 }}>
          <Column sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {counterpartyLabel(data?.counterparty)}
            </Typography>
            {data?.account_owner ? (
              <Typography variant="caption" color="text.secondary">
                Account: {data.account_owner.name}
              </Typography>
            ) : null}
          </Column>
          <IconButton aria-label="Close thread" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Row>

        {loading ? (
          <Row sx={{ justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Row>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!loading && !error && data && !messages.length ? (
          <Typography variant="body2" color="text.secondary">
            No texts in this thread.
          </Typography>
        ) : null}
        {!loading && data?.truncated ? (
          <Alert severity="info">Showing the most recent messages only.</Alert>
        ) : null}
        {!loading
          ? messages.map((message) => (
              <SmsMessageCard
                key={message.id}
                message={threadMessageToCard(message, data?.counterparty)}
              />
            ))
          : null}
      </Column>
    </Drawer>
  )
}

AdminActivityThreadDrawer.propTypes = {
  thread: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
  }),
  onClose: PropTypes.func.isRequired,
}
